<!-- eslint-disable @typescript-eslint/naming-convention -->
<script setup lang="ts">
import { ObjectUtils } from 'primevue/utils';
import { computed, inject, ref, watch } from 'vue';

import {
  BaseTreeProps,
  TreeCheckboxChangeEvent,
  TreeExpandedKeys,
  TreeNode,
  TreeNodeClickEvent,
  TreeSelectionKeys,
} from './BaseTree.vue.d';
import Icon from '../icon/Icon.vue';
import TreeNodeItem from '../treenode/TreeNode.vue';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<BaseTreeProps>(), {
  selectionKeys: null,
  metaKeySelection: false,
  disableNodeAll: false,
  loading: false,
  propagateSelection: true,
  loadingMode: 'mask',
  filterText: '',
  filterBy: 'label',
  filterMode: 'lenient',
  highlightOnSelect: false,
  useOption: false,
  level: 0,
});

const emit = defineEmits<{
  'update:expandedKeys': [value: TreeExpandedKeys];
  'update:selectionKeys': [value: TreeSelectionKeys];
  'nodeSelect': [node: TreeNode];
  'nodeUnselect': [node: TreeNode];
  'nodeExpand': [node: TreeNode];
  'nodeCollapse': [node: TreeNode];
  'toggleMenu': [node: TreeNode];
}>();

const Preset = inject<Record<string, any>>('preset', {}).tree;

const dExpandedKeys = ref(props.expandedKeys ?? []);

const onNodeToggle = (node: TreeNode): void => {
  const { key } = node;

  if (dExpandedKeys.value[key]) {
    delete dExpandedKeys.value[key];
    emit('nodeCollapse', node);
  } else {
    dExpandedKeys.value[key] = true;
    emit('nodeExpand', node);
  }

  dExpandedKeys.value = { ...dExpandedKeys.value };
  emit('update:expandedKeys', dExpandedKeys.value);
};

const onNodeClick = (event: TreeNodeClickEvent): void => {
  if (props.selectionMode != null && event.node.selectable !== false) {
    const metaSelection = event.nodeTouched ? false : props.metaKeySelection;
    const _selectionKeys = metaSelection
      ? handleSelectionWithMetaKey(event)
      : handleSelectionWithoutMetaKey(event);

    emit('update:selectionKeys', _selectionKeys);
  }
};

const onCheckboxChange = (event: TreeCheckboxChangeEvent): void => {
  emit('update:selectionKeys', event.selectionKeys);

  if (event.check) emit('nodeSelect', event.node);
  else emit('nodeUnselect', event.node);
};

const handleSelectionWithMetaKey = (
  event: TreeNodeClickEvent,
): TreeSelectionKeys => {
  const { originalEvent } = event;
  const { node } = event;
  const metaKey = originalEvent.metaKey || originalEvent.ctrlKey;
  const selected = isNodeSelected(node);
  let _selectionKeys;

  if (selected && metaKey) {
    if (isSingleSelectionMode()) {
      _selectionKeys = {};
    } else {
      _selectionKeys = { ...props.selectionKeys };
      delete _selectionKeys[node.key];
    }

    emit('nodeUnselect', node);
  } else {
    if (isSingleSelectionMode()) {
      _selectionKeys = {};
    } else if (isMultipleSelectionMode()) {
      if (metaKey && props.selectionKeys) {
        _selectionKeys = { ...props.selectionKeys };
      } else {
        _selectionKeys = {};
      }
    }

    _selectionKeys[node.key] = true;
    emit('nodeSelect', node);
  }

  return _selectionKeys;
};

const handleSelectionWithoutMetaKey = (
  event: TreeNodeClickEvent,
): TreeSelectionKeys => {
  const { node } = event;
  const selected = isNodeSelected(node);
  let _selectionKeys;

  if (isSingleSelectionMode()) {
    if (selected) {
      _selectionKeys = {};
      emit('nodeUnselect', node);
    } else {
      _selectionKeys = {};
      _selectionKeys[node.key] = true;
      emit('nodeSelect', node);
    }
  } else {
    if (selected) {
      _selectionKeys = { ...props.selectionKeys };
      delete _selectionKeys[node.key];

      emit('nodeUnselect', node);
    } else {
      _selectionKeys = props.selectionKeys ? { ...props.selectionKeys } : {};
      _selectionKeys[node.key] = true;

      emit('nodeSelect', node);
    }
  }

  return _selectionKeys;
};

const isSingleSelectionMode = (): boolean => {
  return props.selectionMode === 'single';
};

const isMultipleSelectionMode = (): boolean => {
  return props.selectionMode === 'multiple';
};

const isNodeSelected = (node: TreeNode): boolean => {
  return props.selectionMode && props.selectionKeys
    ? props.selectionKeys[node.key] === true
    : false;
};

const isNodeLeaf = (node: TreeNode): boolean => {
  return node.leaf === false ? false : !(node.children && node.children.length);
};

const findFilteredNodes = (node: TreeNode, paramsWithoutNode): boolean => {
  if (node) {
    let matched = false;

    if (node.children) {
      const childNodes = [...node.children];

      node.children = [];

      for (const childNode of childNodes) {
        const copyChildNode = { ...childNode };

        if (isFilterMatched(copyChildNode, paramsWithoutNode)) {
          matched = true;
          node.children.push(copyChildNode);
        }
      }
    }

    if (matched) {
      return true;
    }
  }
};

const isFilterMatched = (
  node: TreeNode,
  { searchFields, filterText, strict },
): boolean => {
  let matched = false;

  for (const field of searchFields) {
    const fieldValue = String(
      ObjectUtils.resolveFieldData(node, field),
    ).toLocaleLowerCase(props.filterLocale);

    if (fieldValue.indexOf(filterText) > -1) {
      matched = true;
    }
  }

  if (!matched || (strict && !isNodeLeaf(node))) {
    matched =
      findFilteredNodes(node, { searchFields, filterText, strict }) || matched;
  }

  return matched;
};

const filteredValue = computed<TreeNode[]>(() => {
  const filteredNodes = [];
  const searchFields = props.filterBy.split(',');
  const strict = props.filterMode === 'strict';

  for (const node of props.value) {
    const _node = { ...node };
    const paramsWithoutNode = {
      searchFields,
      filterText: props.filterText.toLocaleLowerCase(props.filterLocale),
      strict,
    };

    if (
      (strict &&
        (findFilteredNodes(_node, paramsWithoutNode) ||
          isFilterMatched(_node, paramsWithoutNode))) ||
      (!strict &&
        (isFilterMatched(_node, paramsWithoutNode) ||
          findFilteredNodes(_node, paramsWithoutNode)))
    ) {
      filteredNodes.push(_node);
    }
  }

  return filteredNodes;
});

watch(
  () => props.expandedKeys,
  (newValue) => {
    dExpandedKeys.value = newValue;
  },
  { deep: true },
);
</script>

<template>
  <div
    v-bind="(props.pt ?? Preset)?.root"
    :id="$attrs['id']"
    :class="$attrs['class']"
    data-wv-name="tree"
  >
    <template v-if="loading && loadingMode === 'mask'">
      <div v-bind="(props.pt ?? Preset)?.loadingOverlay">
        <slot name="loadingicon">
          <Icon
            class="animate-spin"
            icon="spinner"
            v-bind="(props.pt ?? Preset)?.loadingicon"
          />
        </slot>
      </div>
    </template>

    <div
      :style="{ maxHeight: scrollHeight }"
      v-bind="(props.pt ?? Preset)?.wrapper"
    >
      <ul
        :aria-label="ariaLabel"
        :aria-labelledby="ariaLabelledby"
        v-bind="(props.pt ?? Preset)?.container"
        data-pc-section="container"
        role="tree"
      >
        <TreeNodeItem
          :key="node.key"
          v-for="(node, index) of filteredValue"
          v-bind="props"
          :class="[...(props.pt ?? Preset)?.node?.class]"
          :disable-keys="disableKeys"
          :disable-node-all="props.disableNodeAll"
          :disabled="disableKeys?.includes(node.key)"
          :exact-disable-keys="exactDisableKeys"
          :expanded-keys="dExpandedKeys"
          :index="index"
          :level="level + 1"
          :list-labels-no-option="listLabelsNoOption"
          :loading-mode="loadingMode"
          :node="node"
          :propagate-selection="propagateSelection"
          :pt="pt"
          :selection-keys="selectionKeys"
          :selection-mode="selectionMode"
          :show-disposable-groups="showDisposableGroups"
          :templates="$slots"
          :tree-node-menus="treeNodeMenus"
          :unstyled="unstyled"
          :use-option="useOption"
          @checkbox-change="onCheckboxChange"
          @node-click="onNodeClick"
          @node-toggle="onNodeToggle"
          @toggle-menu="$emit('toggleMenu', $event)"
        />
      </ul>
    </div>
  </div>
</template>
